import { useState, type ReactNode } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, Checkbox, Form, Input, Label, Select } from '@/shared/components';
import { extractAllowedOptions, humanizeKey, splitCsv } from '@/shared/components/form/config-value';
import { apiErrorMessage } from '@/shared/utils/apiErrorMessage';
import {
    CONFIG_DATA_TYPES,
    type ConfigDataType,
    type ConfigSettingAddInput,
    type ConfigSettingAddOutput,
    type ConfigSettingAddSchema,
} from './configSettingAddSchema';
import type { AddFormMutations, ConfigOptionLike } from './types';

const DATA_TYPE_OPTIONS = CONFIG_DATA_TYPES.map((dt) => ({ value: dt, label: humanizeKey(dt) }));

// Engine-owned and handed to the picker slot so the Label and rendered control cannot drift apart.
const CATALOG_PICKER_ID = 'catalog-combobox';

interface ConfigSettingsAddFormProps<TOption extends ConfigOptionLike> {
    schema: ConfigSettingAddSchema;
    mutations: AddFormMutations;
    onDone?: () => void;
    /** Option ids already applied; hidden from the search so a single-value setting can't be added twice. */
    excludeOptionIds?: string[];
    /** True (dealers): Key/Data Type stay editable with no catalog option (config_option_id: null).
     *  False (smr, default): key and data type always derive from a catalog selection. */
    manualEntry?: boolean;
    /** `id` must be applied to the rendered control; the engine Label's `htmlFor` points at it. */
    renderCatalogPicker: (picker: {
        id: string;
        value: TOption | null;
        onChange: (option: TOption | null) => void;
        excludeIds: string[];
    }) => ReactNode;
}

export function ConfigSettingsAddForm<TOption extends ConfigOptionLike>({
    schema,
    mutations,
    onDone,
    excludeOptionIds = [],
    manualEntry = false,
    renderCatalogPicker,
}: ConfigSettingsAddFormProps<TOption>) {
    const [selectedOption, setSelectedOption] = useState<TOption | null>(null);

    const { handleSubmit, control, watch, setValue, reset, formState: { isSubmitting, errors } } =
        useForm<ConfigSettingAddInput, unknown, ConfigSettingAddOutput>({
            resolver: zodResolver(schema),
            defaultValues: { key: '', data_type: 'string', raw_value: '' },
        });

    const dataType = watch('data_type');

    const handleCatalogSelect = (option: TOption | null) => {
        setSelectedOption(option);
        // Clear values carried over from a previous option/type so the type-specific widget never shows stale data.
        setValue('raw_value', '');
        if (option) {
            setValue('config_option_id', option.id);
            setValue('key', option.key_name);
            setValue('data_type', option.data_type as ConfigDataType);
        } else {
            setValue('config_option_id', undefined);
            setValue('key', '');
            setValue('data_type', 'string');
        }
    };

    const onSubmit = (data: ConfigSettingAddOutput) => {
        // Reset/close only after the server accepts; a failed create keeps the form open with the error visible.
        mutations.createMutation.mutate(
            {
                key: data.key,
                value: data.value,
                config_option_id: data.config_option_id ?? null,
            },
            {
                onSuccess: () => {
                    reset();
                    setSelectedOption(null);
                    onDone?.();
                },
            },
        );
    };

    const renderValueWidget = (type: ConfigDataType) => {
        if (type === 'boolean') {
            return (
                <Controller
                    name="raw_value"
                    control={control}
                    render={({ field }) => (
                        <Select
                            id="add-value"
                            placeholder="Value"
                            value={field.value}
                            onChange={(v) => field.onChange(v)}
                            options={[
                                { value: 'true', label: 'Yes' },
                                { value: 'false', label: 'No' },
                            ]}
                        />
                    )}
                />
            );
        }
        if (type === 'multiselect') {
            const allowed = extractAllowedOptions(selectedOption);
            return (
                <Controller
                    name="raw_value"
                    control={control}
                    render={({ field }) => {
                        if (allowed.length === 0) {
                            // No declared allowed set: dealers falls back to free-form CSV;
                            // smr surfaces the misconfigured catalog option.
                            if (manualEntry) {
                                return (
                                    <Input
                                        id="add-value"
                                        value={field.value}
                                        onChange={field.onChange}
                                        placeholder="comma,separated,values"
                                    />
                                );
                            }
                            return (
                                <p className="text-sm text-mp-error">
                                    This option defines no selectable values.
                                </p>
                            );
                        }
                        const selected = splitCsv(field.value ?? '');
                        return (
                            <div role="group" aria-label="Available options" className="flex flex-col gap-2">
                                {allowed.map((opt) => (
                                    <Checkbox
                                        key={opt.value}
                                        id={`add-value-${opt.value}`}
                                        label={opt.label}
                                        ariaLabel={opt.label}
                                        checked={selected.includes(opt.value)}
                                        onChange={(checked) => {
                                            const next = checked
                                                ? [...selected, opt.value]
                                                : selected.filter((s) => s !== opt.value);
                                            // Serialise in the option's declared order for stable payloads.
                                            field.onChange(allowed.filter((a) => next.includes(a.value)).map((a) => a.value).join(','));
                                        }}
                                    />
                                ))}
                            </div>
                        );
                    }}
                />
            );
        }
        if (type === 'integer' || type === 'decimal' || type === 'money') {
            return (
                <Controller
                    name="raw_value"
                    control={control}
                    render={({ field }) => (
                        <Input
                            id="add-value"
                            type="number"
                            step={type === 'integer' ? 1 : 0.01}
                            min="0"
                            value={field.value}
                            onChange={field.onChange}
                        />
                    )}
                />
            );
        }
        return (
            <Controller
                name="raw_value"
                control={control}
                render={({ field }) => (
                    <Input id="add-value" value={field.value} onChange={field.onChange} />
                )}
            />
        );
    };

    return (
        <Form className="space-y-3 max-w-lg border-t border-mp-border pt-4" onSubmit={handleSubmit(onSubmit)}>
            <div>
                <Label htmlFor={CATALOG_PICKER_ID}>{manualEntry ? 'Catalog Setting (optional)' : 'Catalog Setting'}</Label>
                {renderCatalogPicker({
                    id: CATALOG_PICKER_ID,
                    value: selectedOption,
                    onChange: handleCatalogSelect,
                    excludeIds: excludeOptionIds,
                })}
                {!manualEntry && errors.key && (
                    <p role="alert" className="text-mp-error text-xs mt-1">{errors.key.message}</p>
                )}
            </div>

            {manualEntry && (
                <div>
                    <Label htmlFor="add-key" required>Key</Label>
                    <Controller
                        name="key"
                        control={control}
                        render={({ field }) => (
                            <Input
                                id="add-key"
                                value={field.value}
                                onChange={field.onChange}
                                disabled={!!selectedOption}
                                placeholder="e.g. max_mileage"
                            />
                        )}
                    />
                    {errors.key && (
                        <p role="alert" className="text-mp-error text-xs mt-1">{errors.key.message}</p>
                    )}
                </div>
            )}

            {manualEntry && (
                <div>
                    <Label htmlFor="add-data-type" required>Data Type</Label>
                    <Controller
                        name="data_type"
                        control={control}
                        render={({ field }) => (
                            <Select
                                id="add-data-type"
                                placeholder="Data Type"
                                value={field.value}
                                onChange={(v) => field.onChange(v)}
                                options={DATA_TYPE_OPTIONS}
                                disabled={!!selectedOption}
                            />
                        )}
                    />
                </div>
            )}

            {(manualEntry || selectedOption) && (
                <div>
                    <Label htmlFor="add-value">Value</Label>
                    {renderValueWidget(dataType)}
                    {errors.raw_value && (
                        <p role="alert" className="text-mp-error text-xs mt-1">{errors.raw_value.message}</p>
                    )}
                </div>
            )}

            {mutations.createMutation.isError && (
                <Alert variant="error">
                    {apiErrorMessage(mutations.createMutation.error, 'Failed to add setting')}
                </Alert>
            )}

            <div className="flex gap-2">
                <Button
                    size="sm"
                    disabled={isSubmitting || mutations.createMutation.isPending}
                >
                    {mutations.createMutation.isPending ? 'Adding…' : 'Add Setting'}
                </Button>
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => { mutations.createMutation.reset(); onDone?.(); }}
                >
                    Cancel
                </Button>
            </div>
        </Form>
    );
}
